import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get, set, isFunction, find } from 'lodash';
import { browserHistory } from 'common/utils/browserHistory';
import {
  hasLimitedGenderOptions,
  cl787SexualityFields,
  scrn7ScreeningsCompletion,
} from 'common/utils/FeatureFlags/flags';
import { Button, validations } from '@unite-us/app-components';
import { isHttpError } from 'common/utils/Error';
import { OverlaySpinner, Spinner } from 'common/spinners';
import findCurrentGroup from 'common/utils/findCurrentGroup';
import { getGroup } from 'common/utils/stateHelpers';
import { validateReduxForm } from 'common/form';
import { getEnumsFromState } from 'common/utils/Enums';
import { getInitialPaymentsFormData } from 'src/components/Insurance/utils/';
import { fetchGroupContact, fetchProfileForms } from 'actions/Contact/Group';
import { fetchGroup } from 'actions/Group';
import { removeSelectedContact } from 'actions/Contact';
import PaymentsInformation from 'src/components/Insurance/PaymentsInformation';
import { DOMAIN_CONSTANTS } from 'src/common/constants';
import enforcedConsentRoute from 'common/utils/Navigation/enforcedConsentRoute';
import { buildAccessibilityMessageFromErrors } from 'common/utils/Form';
import AddSupportingContactDetailsStep from 'pages/components/AddSupportingContactDetailsStep';
import ReactFinalFormBridge from 'common/form/ReactFinalFormBridge';
import Header from './Header';
import BasicInformation from './BasicInformation';
import ContactInformation from './ContactInformation';
import SupplementalInformation from './SupplementalInformation';
import ContactRequirementsForm from './ContactRequirementsForm';
import MilitaryInformation from './MilitaryInformation';
import {
  CONTACT_INFORMATION_FORM,
  COPY_CREATE_CLIENT,
  COPY_UPDATE_CLIENT,
  NOT_FOUND_HEADER_COPY,
  REGULAR_HEADER_COPY,
} from '../constants';
import {
  goToSearchStep,
  formatNewContactDataWithNoInsurance,
  defaultPhoneNumber,
  defaultEmailAddress,
  defaultAddress,
  getInitialValuesFromContact,
} from '../utils';

const initialPhoneNumber = defaultPhoneNumber(true);
const initialEmail = defaultEmailAddress(true);
const initialAddress = defaultAddress({ is_primary: true });

// Helper functions to reduce cyclomatic complexity
const getContactForInitialValues = (contact, selectedContact) => (
  !isEmpty(contact) ? contact : selectedContact
);

const getHeaderText = (existingContact) => (
  existingContact ? COPY_UPDATE_CLIENT : COPY_CREATE_CLIENT
);

const getSubHeaderText = (existingContact) => (
  existingContact ? REGULAR_HEADER_COPY : `${NOT_FOUND_HEADER_COPY} ${REGULAR_HEADER_COPY}`
);

const getSubmittingText = (existingContact) => (
  existingContact ? 'Updating Client...' : 'Creating Client...'
);

// Helper to extract timeslots from contact or selectedContact with fallbacks
const getContactPreferences = (contact) => {
  if (contact?.contact_preferences) return contact.contact_preferences;
  if (contact?.contact_preference) return contact.contact_preference;
  return null;
};

const extractTimeslots = (contact, selectedContact) => {
  const contactPrefs = getContactPreferences(contact);
  if (contactPrefs?.timeslots) return contactPrefs.timeslots;

  const selectedContactPrefs = getContactPreferences(selectedContact);
  if (selectedContactPrefs?.timeslots) return selectedContactPrefs.timeslots;

  return {};
};

// Helper to extract contact preferences with fallback
const extractContactPreferences = (contact) => (
  contact?.contact_preferences || contact?.contact_preference || {}
);

// Helper to check if any dependency has changed
const hasAnyDepChanged = (cachedDeps, currentDeps) => (
  cachedDeps.contactId !== currentDeps.contactId ||
  cachedDeps.selectedContactId !== currentDeps.selectedContactId ||
  cachedDeps.contactDataId !== currentDeps.contactDataId ||
  cachedDeps.preferredLanguages !== currentDeps.preferredLanguages ||
  cachedDeps.selectedPreferredLanguages !== currentDeps.selectedPreferredLanguages ||
  cachedDeps.contactPreferences !== currentDeps.contactPreferences
);

export class AddContactInformationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isFetching: false,
      isSubmitting: false,
      initialPaymentsIds: [],
      profileForms: [],
      showProfileForms: false,
      // From HBH-2009: React Final Form migration
      allLanguages: [],
      userLanguageEdits: {},
      rffContactReqValues: {},
      rffBasicInfoValues: {},
      rffMilitaryInfoValues: {},
      // RFF validation state - track errors to block submission
      rffBasicInfoHasErrors: false,
      rffContactReqHasErrors: false,
      // From main: Accessibility features
      errorCount: 0,
      accessibilityMessage: '',
      accessibilityMessageKey: 0,
    };

    // Form API refs to trigger validation display
    this.basicInfoFormRef = null;
    this.contactReqFormRef = null;

    // Cache for memoizing initial values
    this.cachedInitialValues = null;
    this.cachedDeps = {
      contactId: null,
      selectedContactId: null,
      contactDataId: null,
      preferredLanguages: null,
      selectedPreferredLanguages: null,
      contactPreferences: null,
    };

    // Cache for memoizing contactReqFieldValues
    this.cachedContactReqFieldValues = null;
    this.cachedContactReqDeps = {
      contactId: null,
      selectedContactId: null,
      contactDataId: null,
      timeslots: null,
    };

    this.onBackClick = this.onBackClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.finalizeStep = this.finalizeStep.bind(this);
    this.getInitialValues = this.getInitialValues.bind(this);
    this.renderProfileForms = this.renderProfileForms.bind(this);
    this.renderContactForm = this.renderContactForm.bind(this);
    this.handleBasicInfoFieldChange = this.handleBasicInfoFieldChange.bind(this);
    this.handleContactReqFieldChange = this.handleContactReqFieldChange.bind(this);
    this.handleBasicInfoValidationChange = this.handleBasicInfoValidationChange.bind(this);
    this.handleContactReqValidationChange = this.handleContactReqValidationChange.bind(this);
    this.syncFieldsToReduxForm = this.syncFieldsToReduxForm.bind(this);
  }

  /* eslint-disable react/sort-comp */
  checkInitialValuesDepsChanged(contact, selectedContact, contactData) {
    const currentDeps = {
      contactId: this.extractId(contact),
      selectedContactId: this.extractId(selectedContact),
      contactDataId: this.extractId(contactData),
      preferredLanguages: this.extractPreferredLanguages(contact),
      selectedPreferredLanguages: this.extractPreferredLanguages(selectedContact),
      contactPreferences: JSON.stringify(extractContactPreferences(contact)),
    };

    return hasAnyDepChanged(this.cachedDeps, currentDeps);
  }

  // eslint-disable-next-line class-methods-use-this
  buildInitialValues(contact, selectedContact, contactData, insurances) {
    const contactForInitialValues = getContactForInitialValues(contact, selectedContact);
    const selectedContactData = getInitialValuesFromContact(contactForInitialValues || {}) || {};
    const decodedContactData = getInitialValuesFromContact(contactData) || {};
    const paymentData = getInitialPaymentsFormData(insurances || []) || {};

    return {
      gender: 'undisclosed',
      marital_status: 'undisclosed',
      race: 'undisclosed',
      ethnicity: 'undisclosed',
      sexuality: [],
      phone_numbers: [initialPhoneNumber],
      email_addresses: [initialEmail],
      addresses: [initialAddress],
      ...decodedContactData,
      ...selectedContactData,
      ...paymentData,
    };
  }

  mergeLanguageFields(initialValues, selectedContactData) {
    // Start with initial values
    let result = { ...initialValues };

    // Merge selected contact languages (don't let undefined overwrite defined)
    result = this.mergeSelectedContactLanguages(result, selectedContactData);

    // Preserve user's in-progress language edits if they exist
    result = this.applyUserLanguageEdits(result);

    return result;
  }

  // eslint-disable-next-line class-methods-use-this
  mergeSelectedContactLanguages(values, selectedContactData) {
    const merged = { ...values };
    if (selectedContactData.preferred_spoken_languages && !merged.preferred_spoken_languages) {
      merged.preferred_spoken_languages = selectedContactData.preferred_spoken_languages;
    }
    if (selectedContactData.preferred_written_languages && !merged.preferred_written_languages) {
      merged.preferred_written_languages = selectedContactData.preferred_written_languages;
    }
    return merged;
  }

  applyUserLanguageEdits(values) {
    const { userLanguageEdits } = this.state;
    if (!userLanguageEdits) {
      return values;
    }

    const updated = { ...values };
    if (userLanguageEdits.preferred_spoken_languages !== undefined) {
      updated.preferred_spoken_languages = userLanguageEdits.preferred_spoken_languages;
    }
    if (userLanguageEdits.preferred_written_languages !== undefined) {
      updated.preferred_written_languages = userLanguageEdits.preferred_written_languages;
    }
    return updated;
  }

  updateCachedDeps(contact, selectedContact, contactData) {
    this.cachedDeps = {
      contactId: this.extractId(contact),
      selectedContactId: this.extractId(selectedContact),
      contactDataId: this.extractId(contactData),
      preferredLanguages: this.extractPreferredLanguages(contact),
      selectedPreferredLanguages: this.extractPreferredLanguages(selectedContact),
      contactPreferences: JSON.stringify(extractContactPreferences(contact)),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  extractId(obj) {
    return obj?.id || null;
  }

  // eslint-disable-next-line class-methods-use-this
  extractPreferredLanguages(contact) {
    return contact?.preferred_languages || null;
  }

  getInitialValues() {
    const {
 contact, contactData, selectedContact, insurances,
} = this.props;

    // Memoization: only recalculate if dependencies have changed
    const depsChanged = this.checkInitialValuesDepsChanged(contact, selectedContact, contactData);

    // If dependencies haven't changed and we have cached values, return them
    if (!depsChanged && this.cachedInitialValues) {
      return this.cachedInitialValues;
    }

    // Dependencies changed, recalculate initial values
    const contactForInitialValues = getContactForInitialValues(contact, selectedContact);
    const selectedContactData = getInitialValuesFromContact(contactForInitialValues || {}) || {};
    let initialValues = this.buildInitialValues(contact, selectedContact, contactData, insurances);

    initialValues = this.mergeLanguageFields(initialValues, selectedContactData);

    // Cache the result and dependencies
    this.cachedInitialValues = initialValues;
    this.updateCachedDeps(contact, selectedContact, contactData);

    return initialValues;
  }

  checkContactReqDepsChanged(contact, selectedContact, contactData) {
    const currentDeps = {
      contactId: this.extractId(contact),
      selectedContactId: this.extractId(selectedContact),
      contactDataId: this.extractId(contactData),
      timeslots: JSON.stringify(extractTimeslots(contact, selectedContact)),
    };

    return this.hasContactReqDepChanged(currentDeps);
  }

  hasContactReqDepChanged(currentDeps) {
    return (
      this.cachedContactReqDeps.contactId !== currentDeps.contactId ||
      this.cachedContactReqDeps.selectedContactId !== currentDeps.selectedContactId ||
      this.cachedContactReqDeps.contactDataId !== currentDeps.contactDataId ||
      this.cachedContactReqDeps.timeslots !== currentDeps.timeslots
    );
  }

  extractContactReqFields(allInitialValues) {
    const contactReqFieldValues = {};
    ContactRequirementsForm.fields.forEach((fieldName) => {
      // Check if user has edited this language field
      const hasUserEdit = this.state.userLanguageEdits && fieldName in this.state.userLanguageEdits;

      if (hasUserEdit) {
        // Use the user's edited value, not the stale initial value
        set(contactReqFieldValues, fieldName, this.state.userLanguageEdits[fieldName]);
      } else {
        const value = get(allInitialValues, fieldName);
        if (value !== undefined) {
          set(contactReqFieldValues, fieldName, value);
        }
      }
    });

    // Ensure contact_preferences with timeslots is set completely
    if (allInitialValues.contact_preferences) {
      contactReqFieldValues.contact_preferences = {
        ...contactReqFieldValues.contact_preferences,
        ...allInitialValues.contact_preferences,
      };
    }

    return contactReqFieldValues;
  }

  updateCachedContactReqDeps(contact, selectedContact, contactData) {
    this.cachedContactReqDeps = {
      contactId: contact?.id || null,
      selectedContactId: selectedContact?.id || null,
      contactDataId: contactData?.id || null,
      timeslots: JSON.stringify(extractTimeslots(contact, selectedContact)),
    };
  }

  getContactReqFieldValues() {
    const { contact, contactData, selectedContact } = this.props;

    // Memoization: only recalculate if dependencies have changed
    const depsChanged = this.checkContactReqDepsChanged(contact, selectedContact, contactData);

    // If dependencies haven't changed and we have cached values, return them
    if (!depsChanged && this.cachedContactReqFieldValues) {
      return this.cachedContactReqFieldValues;
    }

    // Dependencies changed, recalculate contactReqFieldValues
    const allInitialValues = this.getInitialValues();
    const contactReqFieldValues = this.extractContactReqFields(allInitialValues);

    // Cache the result and dependencies
    this.cachedContactReqFieldValues = contactReqFieldValues;
    this.updateCachedContactReqDeps(contact, selectedContact, contactData);

    return contactReqFieldValues;
  }

  /**
   * Syncs field values from React Final Form back to Redux Form
   * @param {Array<string>} fieldNames - Array of field names to sync
   * @param {Object} rffValues - Values from React Final Form
   */
  syncFieldsToReduxForm(fieldNames, rffValues) {
    const { fields } = this.props;
    fieldNames.forEach((fieldName) => {
      const value = get(rffValues, fieldName);
      if (value !== undefined) {
        const field = get(fields, fieldName);
        if (field && field.onChange) {
          field.onChange(value);
        }
      }
    });
  }

  handleBasicInfoFieldChange(rffValues) {
    this.setState({ rffBasicInfoValues: rffValues });
    this.syncFieldsToReduxForm(BasicInformation.fields, rffValues);
  }

  handleBasicInfoValidationChange({ hasValidationErrors }) {
    this.setState({ rffBasicInfoHasErrors: hasValidationErrors });
  }

  handleContactReqFieldChange(rffValues) {
    this.setState({ rffContactReqValues: rffValues });
    this.updateLanguageEdits(rffValues);
    this.syncFieldsToReduxForm(ContactRequirementsForm.fields, rffValues);
  }

  handleContactReqValidationChange({ hasValidationErrors }) {
    this.setState({ rffContactReqHasErrors: hasValidationErrors });
  }

  updateLanguageEdits(rffValues) {
    const languageFields = ['preferred_spoken_languages', 'preferred_written_languages'];
    const languageEdits = {};
    languageFields.forEach((fieldName) => {
      const value = get(rffValues, fieldName);
      if (value !== undefined) {
        languageEdits[fieldName] = value;
      }
    });

    this.setState((prevState) => ({
      userLanguageEdits: {
        ...prevState.userLanguageEdits,
        ...languageEdits,
      },
    }));
  }

  renderSupplementalSection(allInitialValues) {
    const {
      completeGroup,
      contactId,
      currentGroup,
      fields,
      registerField,
      untouch,
    } = this.props;

    if (!completeGroup) {
      return <Spinner />;
    }

    return (
      <SupplementalInformation
        contactId={contactId}
        currentGroup={currentGroup}
        fields={fields}
        registerField={registerField}
        untouch={untouch}
        initialValues={allInitialValues}
        onMilitaryInfoChange={(rffValues) => {
          if (JSON.stringify(this.state.rffMilitaryInfoValues) !== JSON.stringify(rffValues)) {
            this.setState({ rffMilitaryInfoValues: rffValues });
          }
        }}
      />
    );
  }

  renderProfileForms() {
    return (
      <div>
        <AddSupportingContactDetailsStep
          activeStep={this.props.activeStep}
          context={this.props.context}
        />
      </div>
    );
  }

  renderContactForm(
    allInitialValues,
    basicInfoFieldValues,
    contactReqFieldValues,
    showErrorSummary,
    accessibilityMessage,
  ) {
    const {
      addressTypeahead,
      completeGroup,
      contactId,
      enums,
      fields,
      integrationContact,
      registerField,
      limitedGenderOptions,
      shouldShowSexualityFields,
    } = this.props;

    const { isSubmitting } = this.state;
    const existingContact = contactId || integrationContact;
    const header = getHeaderText(existingContact);
    const subHeader = getSubHeaderText(existingContact);
    const submittingText = getSubmittingText(existingContact);

    return (
      <div>
        <Header header={header} subHeader={subHeader} />
        <form>
          {showErrorSummary && this.renderErrorSummary(accessibilityMessage)}
          <OverlaySpinner text={submittingText} show={isSubmitting} />
          <OverlaySpinner text="Fetching Client..." show={this.state.isFetching} />
          <ReactFinalFormBridge
            initialValues={basicInfoFieldValues}
            onFieldChange={this.handleBasicInfoFieldChange}
            onValidationChange={this.handleBasicInfoValidationChange}
            formRef={(form) => { this.basicInfoFormRef = form; }}
          >
            <BasicInformation
              enums={enums}
              limitedGenderOptions={limitedGenderOptions}
              shouldShowSexualityFields={shouldShowSexualityFields}
              isExistingContact={!!contactId}
            />
          </ReactFinalFormBridge>
          <ContactInformation
            registerField={registerField}
            fields={fields}
            validations={validations}
            addressTypeahead={addressTypeahead}
          />
          <ReactFinalFormBridge
            initialValues={contactReqFieldValues}
            onFieldChange={this.handleContactReqFieldChange}
            onValidationChange={this.handleContactReqValidationChange}
            formRef={(form) => { this.contactReqFormRef = form; }}
          >
            <ContactRequirementsForm
              requireContactPreferences={completeGroup ? completeGroup.require_contact_preferences : false}
              onLanguagesLoaded={(languages) => {
                this.setState({ allLanguages: languages });
              }}
            />
          </ReactFinalFormBridge>
          {this.renderSupplementalSection(allInitialValues)}
          <footer className="mt-6 text-right add-contact-information-form__footer">
            <Button
              id="go-back-btn"
              onClick={this.onBackClick}
              label="Go Back"
              style={{ marginRight: '10px' }}
              disabled={isSubmitting}
            />
            <Button
              id="save-client-btn"
              onClick={this.onSaveClientClick}
              label="Save Client"
              primary
              disabled={isSubmitting}
            />
          </footer>
        </form>
      </div>
    );
  }
  /* eslint-enable react/sort-comp */

  componentDidMount() {
    const {
      contactId,
      completeGroup,
      groupId,
      insurances,
      selectedContact,
      checkForAttachedForms,
    } = this.props;

    const initialIds = insurances
      .filter((insurance) => insurance.id !== '')
      .map((insurance) => ({
        id: insurance.id,
        plan_type: insurance.relationships.plan.data.planType,
      }));

    this.setInitialPaymentsIds(initialIds);

    if (contactId && isEmpty(selectedContact)) {
      this.setState({ isFetching: true });
      this.props.fetchGroupContact(groupId, contactId).then(() => {
        this.setState({ isFetching: false });
      });
    }

    if (!completeGroup) {
      this.props.fetchGroup(this.props.groupId);
    }

    if (checkForAttachedForms) {
      this.fetchProfileForms();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // From HBH-2009: Language and contact change handlers
    this.handleContactIdChange(prevProps);
    this.handleLanguageChanges(prevProps);
    this.handleInsuranceChanges(prevProps);
    // From main: Accessibility message updates
    this.handleAccessibilityMessageUpdates(prevProps, prevState);
  }

  handleContactIdChange(prevProps) {
    const prevContactId = prevProps.contact?.id || prevProps.selectedContact?.id;
    const currentContactId = this.props.contact?.id || this.props.selectedContact?.id;

    const shouldClearLanguageEdits = (
      prevContactId !== currentContactId &&
      currentContactId
    );

    if (shouldClearLanguageEdits) {
      this.setState({ userLanguageEdits: {} });
    }
  }

  handleLanguageChanges(prevProps) {
    const prevContactId = this.getContactId(prevProps);
    const currentContactId = this.getContactId(this.props);
    const prevLanguages = this.extractPreferredLanguages(prevProps.contact);
    const currentLanguages = this.extractPreferredLanguages(this.props.contact);

    const shouldClearLanguageEdits = this.shouldClearLanguageEditsForContact(
      prevContactId,
      currentContactId,
      prevLanguages,
      currentLanguages,
    );

    if (shouldClearLanguageEdits) {
      this.setState({ userLanguageEdits: {} });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  shouldClearLanguageEditsForContact(prevContactId, currentContactId, prevLanguages, currentLanguages) {
    const isSameContact = prevContactId === currentContactId;
    if (!isSameContact) return false;

    const languagesChanged = prevLanguages !== currentLanguages;
    if (!languagesChanged) return false;

    const hasCurrentLanguages = !!currentLanguages;
    return hasCurrentLanguages;
  }

  // eslint-disable-next-line class-methods-use-this
  getContactId(props) {
    return props.contact?.id || props.selectedContact?.id;
  }

  handleInsuranceChanges(prevProps) {
    if (this.props.insurances !== prevProps.insurances) {
      const currentInitialIds = this.props.insurances
        .filter((insurance) => insurance.id !== '')
        .map((insurance) => ({
          id: insurance.id,
          plan_type: insurance.relationships.plan.data.planType,
        }));

      this.setInitialPaymentsIds(currentInitialIds);
    }
  }

  handleSubmitFailedChanged() {
    this.updateAccessibilityMessage(true, 200, 50);
  }

  handleSubmitFailedCleared() {
    this.setState({ errorCount: 0, accessibilityMessage: '', accessibilityMessageKey: 0 });
  }

  handleMessageClearedButErrorsRemain() {
    this.updateAccessibilityMessage(false, 200, 300);
  }

  handleResubmittingWithErrors() {
    this.updateAccessibilityMessage(true, 300, 300);
  }

  handleAccessibilityMessageUpdates(prevProps, prevState) {
    if (this.checkSubmitFailedChanged(prevProps)) {
      this.handleSubmitFailedChanged();
      return;
    }

    if (this.checkSubmitFailedCleared(prevProps)) {
      this.handleSubmitFailedCleared();
      return;
    }

    if (this.checkMessageClearedButErrorsRemain(prevState)) {
      this.handleMessageClearedButErrorsRemain();
      return;
    }

    if (this.checkResubmittingWithErrors(prevState)) {
      this.handleResubmittingWithErrors();
    }
  }

  onBackClick() {
    const { context, activeStep } = this.props;

    if (isFunction(activeStep.onBackClick)) {
      return activeStep.onBackClick();
    }
    return goToSearchStep({ context });
  }

  validateLanguagesLoaded() {
    return this.state.allLanguages && this.state.allLanguages.length > 0;
  }

  hasRffValidationErrors() {
    return this.state.rffBasicInfoHasErrors || this.state.rffContactReqHasErrors;
  }

  triggerRffValidationDisplay() {
    // Touch all fields in RFF forms to trigger validation error display
    if (this.basicInfoFormRef) {
      this.basicInfoFormRef.mutators.touchAllFields();
    }
    if (this.contactReqFormRef) {
      this.contactReqFormRef.mutators.touchAllFields();
    }
  }

  onSaveClientClick = (event) => {
    this.setState({
      accessibilityMessage: '',
      accessibilityMessageKey: this.state.accessibilityMessageKey + 1,
    });

    // Check for RFF validation errors before submitting
    if (this.hasRffValidationErrors()) {
      // Trigger validation display on RFF fields
      this.triggerRffValidationDisplay();
      // Let Redux Form also run its validation (will trigger submitFailed)
      this.props.handleSubmit(() => Promise.reject())(event);
      return;
    }

    this.props.handleSubmit(this.onSubmit)(event);
  }

  onSubmit(values = {}) {
    const {
      createOrUpdateContact,
      fields,
      checkForAttachedForms,
      contactId,
      context,
    } = this.props;
    const {
      profileForms, rffContactReqValues, rffBasicInfoValues, rffMilitaryInfoValues,
    } = this.state;
    // Merge Redux Form values with React Final Form values
    // RFF values take precedence since those forms are managed by RFF
    const newValues = {
      ...values,
      ...rffBasicInfoValues,
      ...rffContactReqValues,
      ...rffMilitaryInfoValues,
      // Ensure contact_preferences from RFF overwrites Redux Form version
      contact_preferences: {
        ...values.contact_preferences,
        ...rffContactReqValues.contact_preferences,
      },
    };

    this.setState({ isSubmitting: true });

    // Validate that languages are loaded before proceeding
    if (!this.validateLanguagesLoaded()) {
      this.setState({ isSubmitting: false });
      return Promise.resolve(null);
    }

    return createOrUpdateContact(
      formatNewContactDataWithNoInsurance({ fields, values: newValues }),
      fields,
      newValues.insurance,
      this.state.initialPaymentsIds,
      this.state.allLanguages,
    ).then((payload) => {
      this.setState({ isSubmitting: false }, () => {
        // This means something went wrong with createOrUpdateContact, and the page should stay as-is
        if (!payload) { return; }

        if (checkForAttachedForms && this.state.profileForms.length) {
          this.setState({ showProfileForms: true });
        } else if (context.includes(DOMAIN_CONSTANTS.REFERRAL) && contactId != null) {
          browserHistory.push({
            pathname: '/referrals/create/add-resources',
            search: `?person=${contactId}`,
            state: { source: 'client' },
          });
        } else if (!checkForAttachedForms || !profileForms.length) {
          this.finalizeStep(payload, newValues);
        } else {
          this.setState({ showProfileForms: true });
        }
      });
    });
  }

  setInitialPaymentsIds(initialIds) {
    this.setState({
      initialPaymentsIds: initialIds,
    });
  }

  updateAccessibilityMessage = (clearFirst = false, initialDelay = 200, buildDelay = 50) => {
    buildAccessibilityMessageFromErrors({
      getErrorCount: () => Object.keys(this.props.errors || {}).length,
      setState: (updates, callback) => this.setState(updates, callback),
      clearFirst,
      initialDelay,
      buildDelay,
    });
  }

  checkSubmitFailedChanged(prevProps) {
    return !prevProps.submitFailed && this.props.submitFailed;
  }

  checkSubmitFailedCleared(prevProps) {
    return prevProps.submitFailed && !this.props.submitFailed;
  }

  checkMessageClearedButErrorsRemain(prevState) {
    return (
      this.props.submitFailed &&
      !this.state.accessibilityMessage &&
      prevState.accessibilityMessage
    );
  }

  checkResubmittingWithErrors(prevState) {
    return (
      !prevState.isSubmitting &&
      this.state.isSubmitting &&
      this.props.submitFailed
    );
  }

  async fetchProfileForms() {
    const { data: { data } } = await this.props.fetchProfileForms(this.props.groupId);

    if (!data.length) return;

    this.setState({
      profileForms: data,
    });
  }

  finalizeStep(payload) {
    if (!isHttpError(payload)) {
      const contact = get(payload, 'data.data', {});
      const {
        activeStep,
        contactId,
        isClientRoute,
        context,
        scrn7ScreeningsCompletionEnabled,
      } = this.props;

      if (
        scrn7ScreeningsCompletionEnabled &&
        context.includes(DOMAIN_CONSTANTS.SCREENING)
      ) {
        const screeningPath = `/facesheet/${contact?.id}/screenings`;
        enforcedConsentRoute({
          contact,
          itemType: DOMAIN_CONSTANTS.SCREENING,
          to: screeningPath,
        });
      } else if (!isClientRoute && activeStep.onStepComplete) {
        activeStep.onStepComplete({
          contact,
          contactId,
          eventTracker: this.context.eventTracker,
          removeSelectedContact: this.props.removeSelectedContact,
        });
      } else {
        enforcedConsentRoute({
          contact,
          itemType: DOMAIN_CONSTANTS.CONTACT,
          to: `/facesheet/${contact.id}`,
        });
      }
    }
  }

  renderErrorSummary(accessibilityMessage) {
    return (
      <div
        key={this.state.accessibilityMessageKey}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {accessibilityMessage}
      </div>
    );
  }

  render() {
    const { submitFailed } = this.props;

    const { accessibilityMessage } = this.state;

    const allInitialValues = this.getInitialValues();

    // Extract only the fields BasicInformation needs from allInitialValues
    const basicInfoFieldValues = {};
    BasicInformation.fields.forEach((fieldName) => {
      const value = allInitialValues[fieldName];
      if (value !== undefined) {
        basicInfoFieldValues[fieldName] = value;
      }
    });

    // Get cached contactReqFieldValues to prevent constant re-creation
    const contactReqFieldValues = this.getContactReqFieldValues();

    const errorCount = this.state.errorCount;
    const showErrorSummary = submitFailed && errorCount > 0;

    return (
      <div className="add-contact-information-form">
        {this.state.showProfileForms ?
          this.renderProfileForms() :
          this.renderContactForm(
            allInitialValues,
            basicInfoFieldValues,
            contactReqFieldValues,
            showErrorSummary,
            accessibilityMessage,
          )}
      </div>
    );
  }
}

AddContactInformationForm.propTypes = {
  activeStep: PropTypes.object.isRequired,
  completeGroup: PropTypes.object,
  contact: PropTypes.object.isRequired,
  contactData: PropTypes.object,
  contactId: PropTypes.string,
  context: PropTypes.string,
  createOrUpdateContact: PropTypes.func.isRequired,
  currentGroup: PropTypes.object.isRequired,
  enums: PropTypes.object.isRequired,
  fetchGroup: PropTypes.func.isRequired,
  fetchGroupContact: PropTypes.func.isRequired,
  fields: PropTypes.object.isRequired,
  groupId: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  insurances: PropTypes.array,
  integrationContact: PropTypes.bool,
  registerField: PropTypes.func.isRequired,
  removeSelectedContact: PropTypes.func.isRequired,
  selectedContact: PropTypes.object,
  untouch: PropTypes.func.isRequired,
  fetchProfileForms: PropTypes.func.isRequired,
  checkForAttachedForms: PropTypes.bool.isRequired,
  isClientRoute: PropTypes.bool.isRequired,
  addressTypeahead: PropTypes.bool,
  scrn7ScreeningsCompletionEnabled: PropTypes.bool,
  limitedGenderOptions: PropTypes.bool.isRequired,
  shouldShowSexualityFields: PropTypes.bool.isRequired,
  submitFailed: PropTypes.bool,
  errors: PropTypes.object,
};

AddContactInformationForm.defaultProps = {
  addressTypeahead: false,
  completeGroup: null,
  contactData: {},
  contactId: null,
  context: '',
  insurances: [],
  integrationContact: false,
  selectedContact: {},
  scrn7ScreeningsCompletionEnabled: false,
  submitFailed: false,
  errors: {},
};

AddContactInformationForm.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const {
    user,
    session,
    session: { groupId },
  } = state;

  const {
    selectedContact, insurances, contactId,
  } = ownProps;

  const contacts = get(state, 'contacts.contacts', []);
  const searchedContacts = get(state, 'searchedContacts', []);
  const contact =
    find([...searchedContacts, ...contacts], { id: contactId }) || {};

  // Use the freshest contact data available:
  // 1. contact from state (freshly fetched) takes priority
  // 2. fall back to selectedContact from props (from search)
  const contactForInitialValues = getContactForInitialValues(contact, selectedContact);
  const selectedContactData = getInitialValuesFromContact(contactForInitialValues || {}) || {};
  const contactData = getInitialValuesFromContact(ownProps.contactData) || {};
  const paymentData = getInitialPaymentsFormData(insurances || []) || {};

  const initialValues = {
    gender: 'undisclosed',
    marital_status: 'undisclosed',
    race: 'undisclosed',
    ethnicity: 'undisclosed',
    sexuality: [],
    phone_numbers: [initialPhoneNumber],
    email_addresses: [initialEmail],
    addresses: [initialAddress],
    ...contactData,
    ...selectedContactData,
    ...paymentData,
  };

  return {
    completeGroup: getGroup(state, groupId),
    contact,
    contactId,
    currentGroup: findCurrentGroup(user, session),
    enums: getEnumsFromState(state),
    groupId,
    initialValues,
    scrn7ScreeningsCompletionEnabled: scrn7ScreeningsCompletion(state),
    limitedGenderOptions: hasLimitedGenderOptions(state),
    shouldShowSexualityFields: cl787SexualityFields(state),
  };
}

export default validateReduxForm(
  {
    form: CONTACT_INFORMATION_FORM,
    fields: [
      ...BasicInformation.fields,
      ...ContactInformation.fields,
      ...ContactRequirementsForm.fields,
      ...MilitaryInformation.fields,
      ...PaymentsInformation.fields,
    ],
    destroyOnUnmount: true,
    enableReinitialize: true,
  },
  mapStateToProps,
  {
    fetchGroupContact,
    fetchGroup,
    removeSelectedContact,
    fetchProfileForms,
  },
)(AddContactInformationForm);
